import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getFunctions, httpsCallable } from "firebase/functions";
import { RootState } from "../../../setup/redux/Store";
import { writeNotification } from "../../../hooks/useTeamNotifications";

type AcceptResult = {
  success: boolean;
  teamName: string;
  role: string;
  teamId: string;
  alreadyMember?: boolean;
};

const ROLE_LABELS: Record<string, string> = {
  strategist: "Strategist",
  executor: "Executor",
  viewer: "Viewer",
};

const ROLE_COLOURS: Record<string, { bg: string; text: string }> = {
  strategist: { bg: "#EDE9FE", text: "#6D28D9" },
  executor: { bg: "#D1FAE5", text: "#065F46" },
  viewer: { bg: "#F1F5F9", text: "#475569" },
};

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAuthorized = useSelector((state: RootState) => state.auth.user);

  const token = searchParams.get("token") || "";
  const teamParam = searchParams.get("team") || "";
  const roleParam = searchParams.get("role") || "";

  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState<AcceptResult | null>(null);
  const [error, setError] = useState("");

  // Derive display values from URL params (pre-auth, or as fallback)
  const displayTeam = teamParam ? decodeURIComponent(teamParam) : "a team";
  const displayRole = roleParam || "";
  const roleColor = ROLE_COLOURS[displayRole] ?? { bg: "#F1F5F9", text: "#475569" };
  const roleLabel = ROLE_LABELS[displayRole] ?? displayRole;

  // If the token is missing, show an error immediately
  useEffect(() => {
    if (!token) {
      setError("Invalid invite link. Please check the link in your email and try again.");
    }
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setError("");
    try {
      const functions = getFunctions();
      const acceptInvite = httpsCallable<
        { token: string },
        AcceptResult
      >(functions, "acceptTeamInvite");
      const response = await acceptInvite({ token });
      setResult(response.data);

      // Write team_invite notification to the accepted user's feed (fire-and-forget)
      const user = isAuthorized as any;
      if (user?.uid && response.data.success && !response.data.alreadyMember) {
        writeNotification(
          user.uid,
          `team_invite_accepted_${response.data.teamId}`,
          {
            type: "team_invite",
            message: `You joined "${response.data.teamName}" as ${ROLE_LABELS[response.data.role] ?? response.data.role}`,
            read: false,
            createdAt: new Date().toISOString(),
            linkPath: "/team",
          }
        );
      }
    } catch (err: any) {
      const msg = err?.message || "Failed to accept the invite. Please try again.";
      setError(msg);
    } finally {
      setAccepting(false);
    }
  };

  const handleGoToTeam = () => {
    navigate("/team");
  };

  const handleLogin = () => {
    // Store token in sessionStorage so we can redirect back
    sessionStorage.setItem("bf_pending_invite_token", token);
    sessionStorage.setItem("bf_pending_invite_team", teamParam);
    sessionStorage.setItem("bf_pending_invite_role", roleParam);
    navigate("/auth/login");
  };

  const handleRegister = () => {
    sessionStorage.setItem("bf_pending_invite_token", token);
    sessionStorage.setItem("bf_pending_invite_team", teamParam);
    sessionStorage.setItem("bf_pending_invite_role", roleParam);
    navigate("/auth/registration");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F8FAFC",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      {/* Logo / brand */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#6D28D9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="bi bi-people-fill" style={{ color: "#FFFFFF", fontSize: 16 }} />
          </div>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0F172A",
              letterSpacing: "-0.01em",
            }}
          >
            Business Foundry
          </span>
        </div>
      </div>

      {/* Main card */}
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: "1px solid #E2E8F0",
          padding: "40px 36px",
          maxWidth: 460,
          width: "100%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        {/* Missing token */}
        {!token && (
          <div className="text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
              style={{ width: 64, height: 64, background: "#FEE2E2" }}
            >
              <i className="bi bi-x-lg" style={{ fontSize: 24, color: "#DC2626" }} />
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: 20, color: "#0F172A" }}>
              Invalid Invite Link
            </h2>
            <p className="text-muted" style={{ fontSize: 14 }}>
              This invite link is missing required information. Please check the
              link in your email and try again.
            </p>
          </div>
        )}

        {/* Success: accepted */}
        {token && result && result.success && !result.alreadyMember && (
          <div className="text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
              style={{ width: 72, height: 72, background: "#D1FAE5" }}
            >
              <i className="bi bi-check-lg" style={{ fontSize: 32, color: "#065F46" }} />
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: 22, color: "#0F172A" }}>
              You're in!
            </h2>
            <p style={{ fontSize: 14, color: "#475569", marginBottom: 16 }}>
              You've joined{" "}
              <strong>{result.teamName}</strong> as a{" "}
              <span
                style={{
                  display: "inline-block",
                  background: (ROLE_COLOURS[result.role] ?? roleColor).bg,
                  color: (ROLE_COLOURS[result.role] ?? roleColor).text,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                }}
              >
                {ROLE_LABELS[result.role] ?? result.role}
              </span>
              .
            </p>
            <button className="btn btn-primary w-100" onClick={handleGoToTeam}>
              <i className="bi bi-people-fill me-2" />
              Go to Team Dashboard
            </button>
          </div>
        )}

        {/* Success: already a member */}
        {token && result && result.alreadyMember && (
          <div className="text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
              style={{ width: 72, height: 72, background: "#DBEAFE" }}
            >
              <i className="bi bi-people-fill" style={{ fontSize: 28, color: "#1D4ED8" }} />
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: 22, color: "#0F172A" }}>
              You're already a member
            </h2>
            <p style={{ fontSize: 14, color: "#475569", marginBottom: 20 }}>
              You're already a member of <strong>{result.teamName}</strong>.
            </p>
            <button className="btn btn-primary w-100" onClick={handleGoToTeam}>
              <i className="bi bi-people-fill me-2" />
              Go to Team Dashboard
            </button>
          </div>
        )}

        {/* Logged-in: show accept UI */}
        {token && !result && isAuthorized && (
          <div className="text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
              style={{ width: 72, height: 72, background: "#EDE9FE" }}
            >
              <i className="bi bi-envelope-open" style={{ fontSize: 28, color: "#6D28D9" }} />
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: 22, color: "#0F172A" }}>
              Team Invitation
            </h2>
            <p style={{ fontSize: 14, color: "#475569", marginBottom: 8 }}>
              You've been invited to join
            </p>
            <p className="fw-bold mb-3" style={{ fontSize: 18, color: "#0F172A" }}>
              {displayTeam}
            </p>
            {displayRole && (
              <div style={{ marginBottom: 24 }}>
                <span
                  style={{
                    display: "inline-block",
                    background: roleColor.bg,
                    color: roleColor.text,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "4px 12px",
                    borderRadius: 6,
                  }}
                >
                  {roleLabel}
                </span>
              </div>
            )}
            {error && (
              <div
                className="mb-3"
                style={{
                  background: "#FEF2F2",
                  border: "1px solid #FECACA",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  color: "#DC2626",
                  textAlign: "left",
                }}
              >
                {error}
              </div>
            )}
            <button
              className="btn btn-primary w-100 mb-3"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Accepting…
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2" />
                  Accept Invitation
                </>
              )}
            </button>
            <button
              className="btn btn-light w-100"
              onClick={() => navigate("/dashboard")}
              disabled={accepting}
            >
              Decline
            </button>
          </div>
        )}

        {/* Not logged in: prompt to log in */}
        {token && !result && !isAuthorized && (
          <div className="text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
              style={{ width: 72, height: 72, background: "#EDE9FE" }}
            >
              <i className="bi bi-envelope-open" style={{ fontSize: 28, color: "#6D28D9" }} />
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: 22, color: "#0F172A" }}>
              You've been invited
            </h2>
            {displayTeam !== "a team" && (
              <>
                <p style={{ fontSize: 14, color: "#475569", marginBottom: 4 }}>
                  to join
                </p>
                <p className="fw-bold mb-3" style={{ fontSize: 18, color: "#0F172A" }}>
                  {displayTeam}
                </p>
              </>
            )}
            {displayRole && (
              <div style={{ marginBottom: 16 }}>
                <span
                  style={{
                    display: "inline-block",
                    background: roleColor.bg,
                    color: roleColor.text,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "4px 12px",
                    borderRadius: 6,
                  }}
                >
                  {roleLabel}
                </span>
              </div>
            )}
            <p
              style={{
                fontSize: 13,
                color: "#64748B",
                marginBottom: 24,
                lineHeight: 1.6,
              }}
            >
              Log in or create an account to accept this invitation and join
              your team on Business Foundry.
            </p>
            <button
              className="btn btn-primary w-100 mb-2"
              onClick={handleLogin}
            >
              Log In to Accept
            </button>
            <button
              className="btn btn-light w-100"
              onClick={handleRegister}
            >
              Create an Account
            </button>
          </div>
        )}
      </div>

      {/* Footer note */}
      {token && (
        <p
          style={{
            marginTop: 20,
            fontSize: 12,
            color: "#94A3B8",
            textAlign: "center",
          }}
        >
          Invite links expire after 7 days. If you have questions, contact your team owner.
        </p>
      )}
    </div>
  );
}
